
import React, { useState } from 'react';
// Fix: Use namespace import to bypass missing exported member errors in react-router-dom
import * as ReactRouterDom from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { 
  User, Mail, Phone, ArrowLeft, Building2, 
  Briefcase, History, StickyNote, Edit3, Trash2, Calendar,
  ShieldCheck, UserCircle, MapPin, Heart
} from 'lucide-react';
import { Person } from '../types';
import { formatDateSafe } from '../lib/utils';
import { Button } from '../components/ui/Button';
import { PersonFormModal } from '../features/people/PersonFormModal';
import { PersonDeleteDialog } from '../features/people/PersonDeleteDialog';

const { useParams, useNavigate } = ReactRouterDom as any;

const formatPhoneNumber = (phone: string | null) => {
  if (!phone) return '—';
  const cleaned = ('' + phone).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  return phone;
};

const formatFullAddress = (p: Person) => {
  const parts = [];
  if (p.street_address) parts.push(p.street_address);
  const cityStateZip = [p.city, p.state, p.zip_code].filter(Boolean).join(' ');
  if (cityStateZip) parts.push(cityStateZip);
  return parts.length > 0 ? parts.join(', ') : '—';
};

interface UnifiedInvolvement {
  id: string;
  initiative: string;
  engagement: string;
  role: string;
  status: string;
  type: 'staff' | 'participant';
  linkId: string;
}

export default function PersonDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { data: person, isLoading } = useQuery({
    queryKey: ['person-detail', id],
    queryFn: async () => {
      const { data } = await supabase
        .from('people')
        .select('*, organizations(id, name, street_address, city, state, zip_code), affiliations(id, label)')
        .eq('id', id)
        .single();
      return data as Person & { organizations: any, affiliations?: any, notes?: string };
    }
  });

  const { data: history, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['person-history-unified', id],
    queryFn: async () => {
      const { data: participantData } = await supabase
        .from('participation')
        .select('id, status, engagement_id, engagements(name, programs(name))')
        .eq('person_id', id);

      const { data: staffData } = await supabase
        .from('session_staff')
        .select(`
          id, 
          role_id, 
          session_staff_roles(label), 
          sessions(
            name, 
            engagement_id, 
            engagements(name, programs(name))
          )
        `)
        .eq('person_id', id);

      const { data: volunteerData } = await supabase
        .from('session_volunteer_logs')
        .select(`
          id,
          total_hours,
          notes,
          volunteer_types(label),
          sessions(
            name,
            session_datetime,
            engagement_id,
            engagements(name, programs(name))
          )
        `)
        .eq('person_id', id);

      const unified: UnifiedInvolvement[] = [];

      (volunteerData as any[] | null)?.forEach(v => {
        unified.push({
          id: `v-${v.id}`,
          initiative: v.sessions?.engagements?.programs?.name || 'Unknown Program',
          engagement: v.sessions?.engagements?.name || 'Unknown Engagement',
          role: v.volunteer_types?.label || 'Volunteer',
          status: `${v.total_hours} Hours`,
          type: 'participant', // Using participant icon for volunteers for now, or Heart
          linkId: v.sessions?.engagement_id
        });
      });


      (participantData as any[] | null)?.forEach(p => {
        unified.push({
          id: `p-${p.id}`,
          initiative: p.engagements?.programs?.name || 'Unknown Program',
          engagement: p.engagements?.name || 'Unknown Engagement',
          role: 'Participant',
          status: p.status,
          type: 'participant',
          linkId: p.engagement_id
        });
      });

      (staffData as any[] | null)?.forEach(s => {
        const engName = s.sessions?.engagements?.name || 'Unknown Engagement';
        const progName = s.sessions?.engagements?.programs?.name || 'Unknown Program';
        const roleLabel = s.session_staff_roles?.label || 'Staff';
        const engId = s.sessions?.engagement_id;

        const exists = unified.find(u => u.linkId === engId && u.role === roleLabel);
        if (!exists && engId) {
          unified.push({
            id: `s-${s.id}`,
            initiative: progName,
            engagement: engName,
            role: roleLabel,
            status: 'Active',
            type: 'staff',
            linkId: engId
          });
        }
      });

      return unified;
    },
    enabled: !!id
  });

  if (isLoading) return (
    <div className="p-20 text-center flex flex-col items-center gap-6">
       <div className="w-24 h-24 bg-slate-100 rounded-full animate-pulse" />
       <p className="uppercase font-black text-slate-300 animate-pulse tracking-[0.4em] text-[10px]">Resolving Personal Profile...</p>
    </div>
  );
  
  if (!person) return <div className="p-20 text-center uppercase font-black text-rose-500">Identity not discovered</div>;

  return (
    <div className="space-y-10 animate-in fade-in duration-500 pb-24 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <button onClick={() => navigate('/people')} className="p-3 hover:bg-white rounded-md border border-transparent hover:border-slate-200 transition-all text-slate-400">
            <ArrowLeft size={24} />
          </button>
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-slate-900 text-[#FFDB00] rounded-lg flex items-center justify-center font-black text-3xl shadow-xl shrink-0">
              {person.first_name.charAt(0)}{person.last_name.charAt(0)}
            </div>
            <div>
              <p className="text-[10px] font-black text-blue-600 uppercase tracking-[0.3em] leading-none mb-1">person details</p>
              <h1 className="text-4xl font-black text-slate-900 uppercase tracking-tight leading-none">{person.first_name} {person.last_name}</h1>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
           <Button variant="outline" onClick={() => setIsEditModalOpen(true)} icon={<Edit3 size={14} />}>Modify</Button>
           <button onClick={() => setIsDeleteDialogOpen(true)} className="p-2.5 text-rose-400 bg-white border border-slate-200 rounded-md hover:bg-rose-50 transition-all shadow-sm">
             <Trash2 size={18} />
           </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="space-y-6">
          <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden">
             <div className="p-6 bg-slate-50/50 border-b border-slate-100">
               <h3 className="text-[10px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2"><User size={14} /> Profile Attributes</h3>
             </div>
             <div className="p-8 space-y-6">
                <div className="space-y-1">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><Mail size={12} /> Email</p>
                  <p className="text-sm font-bold text-slate-900 lowercase tracking-tight">{person.email || '—'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><Phone size={12} /> Phone</p>
                  <p className="text-sm font-bold text-slate-900 uppercase tracking-tight">{formatPhoneNumber(person.phone)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><MapPin size={12} /> Registered Address</p>
                  <p className="text-sm font-bold text-slate-900 uppercase tracking-tight leading-relaxed">{formatFullAddress(person)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><Calendar size={12} /> Birth Date</p>
                  <p className="text-sm font-bold text-slate-900 uppercase tracking-tight">
                    {formatDateSafe(person.date_of_birth)}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><Briefcase size={12} /> Affiliation</p>
                  <p className="text-sm font-bold text-slate-900 uppercase tracking-tight">{person.affiliations?.label || 'General'}</p>
                </div>
                <div className="pt-6 border-t border-slate-50 space-y-1">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><Building2 size={12} /> Organization</p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-black text-slate-900 uppercase tracking-tight">{person.organizations?.name || 'Unknown'}</p>
                    {person.organizations && (
                      <button onClick={() => navigate(`/organizations/${person.organizations.id}`)} className="text-blue-600 hover:text-blue-800 transition-colors">
                        <ArrowLeft className="rotate-180" size={14} />
                      </button>
                    )}
                  </div>
                </div>
             </div>
          </div>

          <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden">
             <div className="p-6 bg-slate-50/50 border-b border-slate-100">
               <h3 className="text-[10px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2"><StickyNote size={14} /> notes</h3>
             </div>
             <div className="p-8">
               <div className="text-sm font-medium text-slate-600 leading-relaxed bg-slate-50 p-6 rounded-lg border border-slate-100 min-h-[120px] tracking-wide whitespace-pre-wrap shadow-inner">
                 {person.notes || 'No administrative notes recorded for this identity.'}
               </div>
             </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-10">
          <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden min-h-[400px]">
            <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/30">
              <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                <History size={18} className="text-blue-600" /> Interaction Portfolio
              </h2>
              {person.city && (
                <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <MapPin size={12} /> {person.city}{person.state ? `, ${person.state}` : ''}
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 text-[9px] font-black uppercase text-slate-400 tracking-widest border-b border-slate-100">
                    <th className="px-8 py-5">Involvement</th>
                    <th className="px-8 py-5 text-center">Role</th>
                    <th className="px-8 py-5 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {isHistoryLoading ? (
                    [1, 2, 3].map(i => (
                      <tr key={i} className="animate-pulse">
                        <td colSpan={3} className="px-8 py-8"><div className="h-4 bg-slate-50 rounded w-full" /></td>
                      </tr>
                    ))
                  ) : history?.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-8 py-20 text-center">
                        <div className="flex flex-col items-center gap-4 text-slate-300">
                           <Briefcase size={32} />
                           <p className="text-[10px] font-black uppercase tracking-widest">Zero historical interactions discovered</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    history?.map((inv) => (
                      <tr 
                        key={inv.id} 
                        className="hover:bg-slate-50/50 transition-colors group cursor-pointer"
                        onClick={() => navigate(`/engagements/${inv.linkId}`)}
                      >
                        <td className="px-8 py-6">
                          <p className="text-[11px] font-black text-slate-900 uppercase tracking-tight leading-none mb-1">{inv.initiative}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{inv.engagement}</p>
                        </td>
                        <td className="px-8 py-6 text-center">
                           <div className="flex flex-col items-center">
                             {inv.id.startsWith('v-') ? (
                               <Heart size={14} className="text-rose-500 mb-1" />
                             ) : inv.type === 'staff' ? (
                               <ShieldCheck size={14} className="text-blue-500 mb-1" />
                             ) : (
                               <UserCircle size={14} className="text-slate-400 mb-1" />
                             )}
                             <span className={`text-[10px] font-black uppercase tracking-widest ${inv.type === 'staff' ? 'text-blue-600' : 'text-slate-600'}`}>
                               {inv.role}
                             </span>
                           </div>
                        </td>
                        <td className="px-8 py-6 text-right">
                           <span className={`px-2 py-1 rounded text-[9px] font-black uppercase border tracking-tight ${
                             inv.status === 'active' || inv.status === 'Active' 
                               ? 'bg-emerald-50 text-emerald-600 border-emerald-100' 
                               : 'bg-slate-50 text-slate-400 border-slate-100'
                           }`}>
                             {inv.status}
                           </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {isEditModalOpen && (
        <PersonFormModal 
          person={person} 
          onClose={() => setIsEditModalOpen(false)} 
        />
      )}

      {isDeleteDialogOpen && (
        <PersonDeleteDialog 
          person={person} 
          onClose={() => setIsDeleteDialogOpen(false)}
          onDeleted={() => navigate('/people')}
        />
      )}
    </div>
  );
}
